import React, { useCallback, useEffect, useMemo } from 'react'
import MaskedInput, { Mask } from 'react-text-mask'
import { Input, Label } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import { formFieldWrapper } from '../formFieldWrapper'
import styles from './styles.scss'
import { FormFieldCommonProps } from '../../../types'
const cx = classnames.bind(styles)

interface BaseMaskedInputFieldProps extends FormFieldCommonProps {
  value: string
  label?: string
  name?: string
  compact?: boolean
  inputLabel?: string | React.ReactElement
  normalizeOnBlur?: (value: string) => string
  normalizeOnChange?: (value: string) => string
  className?: string
  mask: Mask | ((value: string) => Mask)
  guide?: boolean
  disabled?: boolean
  focused?: boolean
  suffix?: string
  allowDecimal?: boolean
  allowNegative?: boolean
  decimalLimit?: number
  maskOptions?: {
    allowDecimal?: boolean
    allowNegative?: boolean
    decimalLimit?: number
  }
}

const BaseMaskedInputField: React.FC<BaseMaskedInputFieldProps> = ({
  elmId,
  value,
  onChange,
  onBlur,
  placeholder,
  label,
  inputLabel,
  normalizeOnBlur,
  normalizeOnChange,
  showError,
  fluid = true,
  mask,
  compact = false,
  className,
  guide,
  disabled,
  focused,
  suffix,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  allowDecimal: _allowDecimal,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  allowNegative: _allowNegative,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  decimalLimit: _decimalLimit,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  maskOptions: _maskOptions,
  ...rest
}) => {
  const [hasFocus, setHasFocus] = React.useState(focused)
  const [showSuffix, setShowSuffix] = React.useState(!!suffix)

  const onChangeNew = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (normalizeOnChange) {
        e.target.value = normalizeOnChange(e.target.value)
      }
      onChange?.(e)
    },
    [onChange, normalizeOnChange]
  )

  const onBlurNew = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (normalizeOnBlur) {
        e.target.value = normalizeOnBlur(e.target.value)
        onBlur?.(e)
        onChange?.(e) // need to update state as well
      } else {
        onBlur?.(e)
      }
      setHasFocus(false)
    },
    [onBlur, normalizeOnBlur, onChange]
  )

  useEffect(() => {
    if (focused && !!suffix && !hasFocus) {
      setHasFocus(true)
      setShowSuffix(false)
    }
  }, [suffix, focused, hasFocus])

  useEffect(() => {
    if (!!suffix && !showSuffix) {
      setShowSuffix(true)
    }
  }, [suffix, showSuffix])

  const maskNew = useMemo(() => {
    if (!suffix) return mask
    const addSuffix = (
      mask: Array<string | RegExp>
    ): Array<string | RegExp> => {
      if (showSuffix) {
        return suffix ? [...mask, ' ', suffix] : mask
      }
      return mask
    }
    if (typeof mask === 'function') {
      return (value: string) => {
        const newMask = mask(value)
        return addSuffix(newMask as Array<string | RegExp>)
      }
    }
    return addSuffix(mask as Array<string | RegExp>)
  }, [showSuffix, mask, suffix])

  return (
    <Input
      data-elm-id={`${elmId}_input`}
      value={value}
      onChange={onChangeNew}
      onBlur={onBlurNew}
      error={showError}
      placeholder={placeholder}
      fluid={fluid}
      disabled={disabled}
      className={cx(className, {
        compact,
        labeled: inputLabel,
      })}
      label={
        typeof inputLabel === 'string' ? (
          <Label>{inputLabel}</Label>
        ) : (
          inputLabel
        )
      }
      input={
        <MaskedInput
          name={rest.name}
          mask={maskNew}
          guide={guide}
          placeholder={placeholder || label}
          value={value}
          onBlur={onBlurNew}
          onChange={onChangeNew}
          disabled={disabled}
          aria-label={`${elmId}_input`}
          className={cx('masked-input')}
        />
      }
      {...rest}
    />
  )
}

export default formFieldWrapper((fieldProps: any, showError: boolean) => (
  <BaseMaskedInputField {...fieldProps} showError={showError} />
))
